import { useState, useEffect, useRef } from 'react';
import { readOrgFile, writeOrgFile, listOrgCollection } from '../../storage/FileSystemAdapter';
import PeopleSelector, { resolvePerson } from '../shared/PeopleSelector';
import EvidenceSelector from '../shared/EvidenceSelector';
import ConfirmDialog from '../shared/ConfirmDialog';
import InfoBadge from '../shared/InfoBadge';
import TagEditor from '../shared/TagEditor';
import { BG_TABLE, BG_SUBTLE, BG_SHELL, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_NEUTRAL, FIELD_ERROR_BORDER, UNSAVED_AMBER, SURFACE_WHITE, FONT_PAGE_TITLE, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON } from '../../theme';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:              'Alcance del SGSI',
    save:               'Guardar',
    saving:             'Guardando…',
    saveOk:             '✓ Guardado',
    saveError:          'Error al guardar',
    unsavedLabel:       'Cambios sin guardar',
    unsavedConfirm:     'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving: 'Salir sin guardar',
    saveAndLeave:       'Guardar y salir',
    loading:            'Cargando…',
    validationError:    'Por favor completa los campos obligatorios marcados con *.',
    sScope:             'Declaración de alcance',
    ttScope:            'Declaración formal del alcance del SGSI. Texto narrativo principal del documento de alcance que la auditoría revisa primero (cláusula 4.3).',
    sInclusions:        'Inclusiones',
    sLocations:         'Ubicaciones',
    ttLocations:        'Sedes, oficinas o ubicaciones geográficas dentro del alcance.',
    sServices:          'Servicios',
    ttServices:         'Servicios prestados a clientes (productos, plataformas) o consumidos críticos para la operación dentro del alcance del SGSI. Distinto de las tecnologías generales registradas en Settings.',
    sExclusions:        'Exclusiones',
    ttExclusions:       'Las exclusiones deben estar justificadas.',
    colItem:            'Exclusión',
    colJustification:   'Justificación',
    colActions:         'Acciones',
    addExclusion:       '+ Añadir exclusión',
    excFormTitleNew:    'Nueva exclusión',
    fExcItem:           'Exclusión',
    fExcJustification:  'Justificación',
    excFormSave:        'Guardar',
    excFormCancel:      'Cancelar',
    deleteExcConfirm:   '¿Eliminar esta exclusión?',
    deleteBtn:          '✕',
    excValidation:      'La exclusión y la justificación son obligatorias.',
    sInterfaces:        'Interfaces con entidades externas',
    sOutsourcedProcesses:       'Procesos subcontratados',
    ttOutsourcedProcesses: 'Procesos del alcance ejecutados por terceros. Cada proceso debe tener al menos un proveedor vinculado.',
    colProcess:         'Proceso',
    colSuppliers:       'Proveedores',
    addOutsourcedProcess:'+ Añadir proceso subcontratado',
    opFormTitleNew:     'Nuevo proceso subcontratado',
    fOpProcess:         'Proceso',
    fOpSuppliers:       'IDs de proveedores',
    opFormSave:         'Guardar',
    opFormCancel:       'Cancelar',
    deleteOpConfirm:    '¿Eliminar este proceso subcontratado?',
    opValidation:       'El proceso y al menos un proveedor son obligatorios.',
    opSupplierHint:     'Proveedores disponibles:',
    opNoSuppliers:      'Sin proveedores',
    opSupplierIdPlaceholder: 'ID de proveedor (p.ej. SUP-2026-001)',
    fLinkedAssetIds:    'Activos vinculados',
    fLinkedSupplierIds: 'Proveedores vinculados',
    assetIdPlaceholder: 'ID de activo (p.ej. AST-2026-001)',
    sApproval:          'Versión y aprobación',
    fVersion:           'Versión',
    fApprovedBy:            'Aprobado por',
    fApprovalDate:          'Fecha de aprobación',
    fApprovalEvidenceId:    'ID evidencia de aprobación',
    fNextReview:        'Próxima revisión',
    ttInterfaces:       'Conexiones entre el SGSI y sistemas o partes externas. Ej. integraciones API con SaaS de terceros, correo con clientes, acceso remoto de proveedores.',
    ttInclusions:       'Elementos explícitamente incluidos en el alcance. Ej. todos los empleados, sistemas que procesan datos personales, oficinas propias.',
    ttChangeHistory:    'Cada entrada nueva actualiza automáticamente la versión del documento y recalcula la próxima fecha de revisión.',
    ttApproval:         'Aprobación formal del documento de alcance: firmantes, fecha y evidencia. Recalcula la próxima revisión.',
    sChangeHistory:     'Historial de versiones',
    chColVersion:       'Versión',
    chColDate:          'Fecha',
    chColAuthor:        'Autor',
    chColSummary:       'Resumen',
    chAddEntry:         'Añadir',
    tagPlaceholder:     'Añadir y pulsar Enter…',
    required:           '*',
    requiredLegend:     '* Campos obligatorios',
  },
  en: {
    title:              'ISMS scope',
    save:               'Save',
    saving:             'Saving…',
    saveOk:             '✓ Saved',
    saveError:          'Save error',
    unsavedLabel:       'Unsaved changes',
    unsavedConfirm:     'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving: 'Leave without saving',
    saveAndLeave:       'Save and leave',
    loading:            'Loading…',
    validationError:    'Please complete the required fields marked with *.',
    sScope:             'Scope statement',
    ttScope:            'Formal statement of the ISMS scope. The main narrative text of the scope document that auditors review first (clause 4.3).',
    sInclusions:        'Inclusions',
    sLocations:         'Locations',
    ttLocations:        'Sites, offices, or geographic locations within the scope.',
    sServices:          'Services',
    ttServices:         'Services delivered to customers (products, platforms) or consumed as critical for operations within the ISMS scope. Distinct from the general technologies registered in Settings.',
    sExclusions:        'Exclusions',
    ttExclusions:       'Exclusions must be justified.',
    colItem:            'Exclusion',
    colJustification:   'Justification',
    colActions:         'Actions',
    addExclusion:       '+ Add exclusion',
    excFormTitleNew:    'New exclusion',
    fExcItem:           'Exclusion',
    fExcJustification:  'Justification',
    excFormSave:        'Save',
    excFormCancel:      'Cancel',
    deleteExcConfirm:   'Delete this exclusion?',
    deleteBtn:          '✕',
    excValidation:      'Exclusion and justification are required.',
    sInterfaces:        'Interfaces with external entities',
    sOutsourcedProcesses:       'Outsourced processes',
    ttOutsourcedProcesses: 'Scope processes performed by third parties. Each process must have at least one linked supplier.',
    colProcess:         'Process',
    colSuppliers:       'Suppliers',
    addOutsourcedProcess:'+ Add outsourced process',
    opFormTitleNew:     'New outsourced process',
    fOpProcess:         'Process',
    fOpSuppliers:       'Supplier IDs',
    opFormSave:         'Save',
    opFormCancel:       'Cancel',
    deleteOpConfirm:    'Delete this outsourced process?',
    opValidation:       'Process and at least one supplier are required.',
    opSupplierHint:     'Available suppliers:',
    opNoSuppliers:      'No suppliers',
    opSupplierIdPlaceholder: 'Supplier ID (e.g. SUP-2026-001)',
    fLinkedAssetIds:    'Linked assets',
    fLinkedSupplierIds: 'Linked suppliers',
    assetIdPlaceholder: 'Asset ID (e.g. AST-2026-001)',
    sApproval:          'Version and approval',
    fVersion:           'Version',
    fApprovedBy:            'Approved by',
    fApprovalDate:          'Approval date',
    fApprovalEvidenceId:    'Approval evidence ID',
    fNextReview:        'Next review date',
    ttInterfaces:       'Connections between the ISMS and external systems or parties. E.g. API integrations with third-party SaaS, email with clients, remote supplier access.',
    ttInclusions:       'Elements explicitly included in the scope. E.g. all employees, systems processing personal data, owned offices.',
    ttChangeHistory:    'Each new entry automatically updates the document version and recalculates the next review date.',
    ttApproval:         'Formal approval of the scope document: signatories, date, and evidence. Recalculates the next review date.',
    sChangeHistory:     'Version history',
    chColVersion:       'Version',
    chColDate:          'Date',
    chColAuthor:        'Author',
    chColSummary:       'Summary',
    chAddEntry:         'Add',
    tagPlaceholder:     'Add and press Enter…',
    required:           '*',
    requiredLegend:     '* Required fields',
  },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function defaultData(orgId) {
  return {
    id:             `scope-${orgId}`,
    orgId,
    description:    '',
    inclusions:     [],
    locations:      [],
    services:       [],
    exclusions:     [],
    interfaces:     [],
    outsourcedProcesses: [],
    linkedAssetIds:      [],
    linkedSupplierIds:   [],
    changeHistory:  [],
    version:        '1.0',
    approvedBy:     [],
    approvalDate:        null,
    approvalEvidenceId:  null,
    nextReviewDate:      '',
  };
}

function genOutsourcedProcessId() {
  return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function isOverdue(dateStr) {
  return dateStr && new Date(dateStr) < new Date();
}

function addMonths(dateStr, months) {
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

// Auto-derive Scope.version from changeHistory: the entry with the most recent
// date determines the version. Returns the fallback when no valid dated entries exist.
function deriveVersion(changeHistory, fallback) {
  if (!Array.isArray(changeHistory) || changeHistory.length === 0) return fallback;
  let best = null;
  for (const e of changeHistory) {
    if (!e || !e.date) continue;
    if (!best || e.date >= best.date) best = e;
  }
  return best && best.version ? best.version : fallback;
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const inputSt = (err) => ({
  ...FONT_LABEL,
  width: '100%', boxSizing: 'border-box',
  padding: '0.45rem 0.65rem',
  border: `1px solid ${err ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
  borderRadius: 6,
  background: BG_SURFACE, color: TEXT_DEFAULT, outline: 'none',
});

const labelSt = {
  ...FONT_SMALL,
  color: TEXT_MUTED, letterSpacing: '0.03em',
  textTransform: 'uppercase', display: 'block', marginBottom: '0.3rem',
};

const helperSt = {
  ...FONT_LABEL,
  color: TEXT_DISABLED, marginBottom: '0.6rem', lineHeight: 1.5,
};

// ── SECTION TITLE ─────────────────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <div style={{
      ...FONT_BODY_STRONG,
      color: TEXT_HEADING,
      marginBottom: '1rem', paddingBottom: '0.5rem',
      borderBottom: `1px solid ${BORDER_DEFAULT}`,
    }}>
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{ borderTop: `1px solid ${BORDER_DEFAULT}`, margin: '1.75rem 0' }} />;
}

// ── SCOPE VIEW ────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function ScopeView({ org, lang, onBack, onDirtyChange, saveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;

  const [data, setData]             = useState(null);
  const [dirty, setDirty]           = useState(false);
  const [saveState, setSaveState]   = useState('idle');
  const [errors, setErrors]         = useState({});
  const [excFormOpen, setExcFormOpen] = useState(false);
  const [excForm, setExcForm]       = useState({ item: '', justification: '' });
  const [excFormErr, setExcFormErr] = useState(false);
  const [opFormOpen, setOpFormOpen] = useState(false);
  const [opForm, setOpForm]         = useState({ process: '', linkedSupplierIds: [] });
  const [opFormErr, setOpFormErr]   = useState(false);
  const [suppliers, setSuppliers]   = useState([]);
  const [people, setPeople]         = useState([]);
  const [confirmState, setConfirmState] = useState(null);

  // ── ChangeHistory add-entry form state (ephemeral) ───────────────────────
  const [chVersion, setChVersion]   = useState('1.0');
  const [chDate, setChDate]         = useState(todayStr());
  const [chAuthor, setChAuthor]     = useState('');
  const [chSummary, setChSummary]   = useState('');
  const chVersionInitedRef          = useRef(false);

  // ── Load ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    readOrgFile(org.id, 'scope.json')
      .then(d => setData({ ...defaultData(org.id), changeHistory: [], ...d }))
      .catch(() => setData(defaultData(org.id)));
    listOrgCollection(org.id, 'suppliers')
      .then(list => setSuppliers(Array.isArray(list) ? list : []))
      .catch(() => setSuppliers([]));
  }, [org.id]);

  // ── Load people roster (used by resolvePerson in CH table) ──────────────
  useEffect(() => {
    readOrgFile(org.id, 'people.json')
      .then(d => setPeople(Array.isArray(d.people) ? d.people : []))
      .catch(() => setPeople([]));
  }, [org.id]);

  // ── Pre-fill chVersion from loaded scope.version (one-time) ──────────────
  useEffect(() => {
    if (data && !chVersionInitedRef.current) {
      setChVersion(data.version || '1.0');
      chVersionInitedRef.current = true;
    }
  }, [data]);

  // ── beforeunload ──────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const set = (field, val) => {
    setDirty(true);
    setData(prev => prev ? { ...prev, [field]: val } : prev);
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  // Returns true on successful write, false on validation/save error. The
  // user-facing Save button calls handleSave (no return value consumed).
  // The App-level guard relies on the boolean via the saveRequestTick /
  // onSaveResult contract (see GRC_UI_PATTERNS.md §5).
  const doSave = async () => {
    if (!data || saveState === 'saving') return false;
    if (!data.description.trim()) {
      setErrors({ description: true });
      return false;
    }
    setErrors({});
    setSaveState('saving');
    try {
      const updated = { ...data, updatedAt: new Date().toISOString() };
      await writeOrgFile(org.id, 'scope.json', updated);
      setData(updated);
      setDirty(false);
      setSaveState('ok');
      setTimeout(() => setSaveState('idle'), 2000);
      return true;
    } catch {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
      return false;
    }
  };

  const handleSave = () => { doSave(); };

  // ── App-level guard: save on tick increase, report result back ───────────
  const lastProcessedTickRef = useRef(0);
  useEffect(() => {
    if (typeof saveRequestTick !== 'number') return;
    if (saveRequestTick <= lastProcessedTickRef.current) return;
    lastProcessedTickRef.current = saveRequestTick;
    (async () => {
      const ok = await doSave();
      onSaveResult?.(ok);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  // ── Exclusions ────────────────────────────────────────────────────────────
  const handleAddExclusion = () => {
    if (!excForm.item.trim() || !excForm.justification.trim()) {
      setExcFormErr(true);
      return;
    }
    setExcFormErr(false);
    set('exclusions', [...data.exclusions, {
      item: excForm.item.trim(),
      justification: excForm.justification.trim(),
    }]);
    setExcForm({ item: '', justification: '' });
    setExcFormOpen(false);
  };

  const handleDeleteExclusion = (idx) => {
    setConfirmState({
      message:      t.deleteExcConfirm,
      confirmLabel: t.deleteBtn,
      onConfirm: () => {
        setConfirmState(null);
        set('exclusions', data.exclusions.filter((_, i) => i !== idx));
      },
    });
  };

  // ── Outsourced processes ─────────────────────────────────────────────────
  const handleAddOutsourcedProcess = () => {
    if (!opForm.process.trim() || opForm.linkedSupplierIds.length === 0) {
      setOpFormErr(true);
      return;
    }
    setOpFormErr(false);
    set('outsourcedProcesses', [...(data.outsourcedProcesses || []), {
      id: genOutsourcedProcessId(),
      process: opForm.process.trim(),
      linkedSupplierIds: opForm.linkedSupplierIds,
    }]);
    setOpForm({ process: '', linkedSupplierIds: [] });
    setOpFormOpen(false);
  };

  const handleDeleteOutsourcedProcess = (id) => {
    setConfirmState({
      message:      t.deleteOpConfirm,
      confirmLabel: t.deleteBtn,
      onConfirm: () => {
        setConfirmState(null);
        set('outsourcedProcesses', (data.outsourcedProcesses || []).filter((op) => op.id !== id));
      },
    });
  };

  // ── Change history: add entry ─────────────────────────────────────────────
  // Adding an entry auto-syncs Scope.version (most recent entry wins) and
  // recalculates nextReviewDate as max(CH dates) + org.documentReviewPeriodMonths.
  const handleAddChangeEntry = () => {
    if (!chVersion.trim() || !chDate || !chAuthor) return;
    const entry = {
      version: chVersion.trim(),
      date:    chDate,
      author:  chAuthor,
      summary: chSummary.trim(),
    };
    const updatedCh = [...(data.changeHistory || []), entry];
    const newVersion = deriveVersion(updatedCh, data.version || '1.0');
    const period = (org && org.documentReviewPeriodMonths) || 12;
    const dates = updatedCh.map(e => e && e.date).filter(Boolean);
    const maxDate = dates.length > 0 ? dates.reduce((a, b) => (a >= b ? a : b)) : '';
    const newNextReview = maxDate ? addMonths(maxDate, period) : '';
    set('changeHistory', updatedCh);
    set('version', newVersion);
    set('nextReviewDate', newNextReview);
    setChVersion(newVersion);
    setChDate(todayStr());
    setChAuthor('');
    setChSummary('');
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (!data) {
    return (
      <div style={{ ...FONT_LABEL, padding: '2rem', color: TEXT_MUTED }}>
        {t.loading}
      </div>
    );
  }

  const saveLabel = saveState === 'saving' ? t.saving
    : saveState === 'ok'    ? t.saveOk
    : t.save;

  const isSaving = saveState === 'saving';

  const errorBannerSt = {
    ...FONT_LABEL,
    background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`,
    borderRadius: 6, padding: '0.35rem 0.75rem',
    marginBottom: '1rem',
  };

  const cardSt = {
    background: BG_SURFACE, border: `1px solid ${STATUS_NEUTRAL.bg}`,
    borderRadius: 10, padding: '1.5rem 1.75rem',
  };

  const thSt = {
    ...FONT_TINY,
    color: TEXT_MUTED,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    padding: '0.4rem 0.6rem',
    textAlign: 'left',
    borderBottom: `1px solid ${BORDER_DEFAULT}`,
    background: BG_SUBTLE,
    whiteSpace: 'nowrap',
  };

  const tdSt = {
    padding: '0.45rem 0.6rem',
    borderBottom: `1px solid ${BG_SHELL}`,
    ...FONT_LABEL,
    color: TEXT_DEFAULT,
    verticalAlign: 'middle',
  };

  const chInputSt = {
    width: '100%',
    ...FONT_LABEL,
    border: `1px solid ${BORDER_DEFAULT}`,
    borderRadius: 4,
    padding: '0.25rem 0.4rem',
    background: BG_SURFACE,
    color: TEXT_DEFAULT,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.75rem', marginBottom: '2rem',
      }}>
        <div>
          <h2 style={{
            ...FONT_PAGE_TITLE,
            color: TEXT_HEADING, margin: 0,
          }}>{t.title}</h2>
        </div>
      </div>

      <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 2rem', letterSpacing: '0.04em' }}>{t.requiredLegend}</p>

      {hasErrors && (
        <div style={{
          ...FONT_LABEL,
          background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`,
          borderRadius: 8, padding: '0.75rem 1rem',
          marginBottom: '1.25rem',
          color: ERROR_TEXT,
        }}>{t.validationError}</div>
      )}

      {saveState === 'error' && (
        <div style={errorBannerSt}>{t.saveError}</div>
      )}

      <fieldset disabled={isSaving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* Section 1 — Scope statement */}
        <div style={cardSt}>
          <SectionTitle>
            {t.sScope} <span style={{ color: ERROR_TEXT }}>{t.required}</span>
            <InfoBadge text={t.ttScope} />
          </SectionTitle>
          <textarea
            rows={4}
            value={data.description}
            onChange={e => set('description', e.target.value)}
            style={{ ...inputSt(errors.description), resize: 'vertical' }}
          />
        </div>

        {/* Section 2 — Locations */}
        <div style={cardSt}>
          <SectionTitle>
            {t.sLocations}
            <InfoBadge text={t.ttLocations} />
          </SectionTitle>
          <TagEditor
            tags={data.locations || []}
            onChange={val => set('locations', val)}
            placeholder={t.tagPlaceholder}
          />
        </div>

        {/* Section 3 — Inclusions */}
        <div style={cardSt}>
          <SectionTitle>
            {t.sInclusions}
            <InfoBadge text={t.ttInclusions} />
          </SectionTitle>
          <TagEditor
            tags={data.inclusions}
            onChange={val => set('inclusions', val)}
            placeholder={t.tagPlaceholder}
          />
        </div>

        {/* Section 4 — Exclusions */}
        <div style={cardSt}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <SectionTitle>{t.sExclusions} <InfoBadge text={t.ttExclusions} /></SectionTitle>
            <button
              onClick={() => { setExcFormOpen(true); setExcForm({ item: '', justification: '' }); setExcFormErr(false); }}
              disabled={excFormOpen}
              style={{
                ...FONT_LABEL,
                background: TEXT_HEADING, color: SURFACE_WHITE, border: 'none',
                borderRadius: 7, padding: '0.3rem 0.8rem', cursor: excFormOpen ? 'not-allowed' : 'pointer',
                opacity: excFormOpen ? 0.5 : 1,
              }}
            >{t.addExclusion}</button>
          </div>

          {data.exclusions.length > 0 && (
            <div style={{ overflowX: 'auto', marginBottom: excFormOpen ? '1rem' : 0 }}>
              <table style={{
                ...FONT_LABEL,
                width: '100%', borderCollapse: 'collapse',
              }}>
                <thead>
                  <tr style={{ background: BG_TABLE }}>
                    {[t.colItem, t.colJustification, t.colActions].map(col => (
                      <th key={col} style={{
                        padding: '0.45rem 0.6rem', textAlign: 'left',
                        fontWeight: 600, color: TEXT_MUTED, fontSize: '0.68rem',
                        letterSpacing: '0.03em', borderBottom: `1px solid ${STATUS_NEUTRAL.bg}`,
                      }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.exclusions.map((exc, idx) => (
                    <tr key={idx} style={{ borderBottom: `1px solid ${BG_MUTED}` }}>
                      <td style={{ padding: '0.45rem 0.6rem', color: TEXT_HEADING, maxWidth: 220 }}>{exc.item}</td>
                      <td style={{ padding: '0.45rem 0.6rem', color: TEXT_DEFAULT, maxWidth: 300 }}>{exc.justification}</td>
                      <td style={{ padding: '0.45rem 0.6rem', whiteSpace: 'nowrap' }}>
                        <button
                          onClick={() => handleDeleteExclusion(idx)}
                          style={{
                            ...BTN_DESTRUCTIVE_OUTLINE,
                            ...FONT_SMALL,
                            borderRadius: 5, padding: '0.2rem 0.4rem',
                            cursor: 'pointer',
                          }}
                        >{t.deleteBtn}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {excFormOpen && (
            <div style={{
              background: BG_TABLE, border: `1px solid ${STATUS_NEUTRAL.bg}`,
              borderRadius: 8, padding: '1rem', marginTop: data.exclusions.length > 0 ? '0.5rem' : 0,
            }}>
              <p style={{
                ...FONT_BODY_STRONG,
                color: TEXT_HEADING, margin: '0 0 0.75rem',
              }}>{t.excFormTitleNew}</p>
              <div style={{ marginBottom: '0.6rem' }}>
                <label style={labelSt}>{t.fExcItem} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                <input
                  type="text" value={excForm.item}
                  onChange={e => setExcForm(f => ({ ...f, item: e.target.value }))}
                  style={inputSt(excFormErr && !excForm.item.trim())}
                />
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={labelSt}>{t.fExcJustification} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                <input
                  type="text" value={excForm.justification}
                  onChange={e => setExcForm(f => ({ ...f, justification: e.target.value }))}
                  style={inputSt(excFormErr && !excForm.justification.trim())}
                />
              </div>
              {excFormErr && (
                <p style={{ ...FONT_LABEL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>
                  {t.excValidation}
                </p>
              )}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleAddExclusion}
                  style={{
                    ...BTN_PRIMARY,
                    ...FONT_BUTTON,
                    border: 'none',
                    borderRadius: 6, padding: '0.38rem 1rem', cursor: 'pointer',
                  }}
                >{t.excFormSave}</button>
                <button
                  onClick={() => { setExcFormOpen(false); setExcFormErr(false); }}
                  style={{
                    ...BTN_SECONDARY,
                    ...FONT_LABEL,
                    borderRadius: 6,
                    padding: '0.38rem 0.85rem', cursor: 'pointer',
                  }}
                >{t.excFormCancel}</button>
              </div>
            </div>
          )}
        </div>

        {/* Section 5 — Services */}
        <div style={cardSt}>
          <SectionTitle>
            {t.sServices}
            <InfoBadge text={t.ttServices} />
          </SectionTitle>
          <TagEditor
            tags={data.services || []}
            onChange={val => set('services', val)}
            placeholder={t.tagPlaceholder}
          />
        </div>

        {/* Section 6 — Interfaces */}
        <div style={cardSt}>
          <SectionTitle>
            {t.sInterfaces}
            <InfoBadge text={t.ttInterfaces} />
          </SectionTitle>
          <TagEditor
            tags={data.interfaces}
            onChange={val => set('interfaces', val)}
            placeholder={t.tagPlaceholder}
          />
        </div>

        {/* Section 7 — Outsourced processes */}
        <div style={cardSt}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <SectionTitle>{t.sOutsourcedProcesses} <InfoBadge text={t.ttOutsourcedProcesses} /></SectionTitle>
            <button
              onClick={() => { setOpFormOpen(true); setOpForm({ process: '', linkedSupplierIds: [] }); setOpFormErr(false); }}
              disabled={opFormOpen}
              style={{
                ...FONT_LABEL,
                background: TEXT_HEADING, color: SURFACE_WHITE, border: 'none',
                borderRadius: 7, padding: '0.3rem 0.8rem', cursor: opFormOpen ? 'not-allowed' : 'pointer',
                opacity: opFormOpen ? 0.5 : 1,
              }}
            >{t.addOutsourcedProcess}</button>
          </div>

          {(data.outsourcedProcesses || []).length > 0 && (
            <div style={{ overflowX: 'auto', marginBottom: opFormOpen ? '1rem' : 0 }}>
              <table style={{
                ...FONT_LABEL,
                width: '100%', borderCollapse: 'collapse',
              }}>
                <thead>
                  <tr style={{ background: BG_TABLE }}>
                    {[t.colProcess, t.colSuppliers, t.colActions].map(col => (
                      <th key={col} style={{
                        padding: '0.45rem 0.6rem', textAlign: 'left',
                        fontWeight: 600, color: TEXT_MUTED, fontSize: '0.68rem',
                        letterSpacing: '0.03em', borderBottom: `1px solid ${STATUS_NEUTRAL.bg}`,
                      }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data.outsourcedProcesses || []).map((op) => (
                    <tr key={op.id} style={{ borderBottom: `1px solid ${BG_MUTED}` }}>
                      <td style={{ padding: '0.45rem 0.6rem', color: TEXT_HEADING, maxWidth: 220 }}>{op.process}</td>
                      <td style={{ padding: '0.45rem 0.6rem', color: TEXT_DEFAULT }}>
                        {op.linkedSupplierIds && op.linkedSupplierIds.length > 0
                          ? op.linkedSupplierIds.map(sid => {
                              const match = suppliers.find(s => s.id === sid || s.ref === sid);
                              return match ? `${match.ref || match.id} (${match.name})` : sid;
                            }).join(', ')
                          : <span style={{ color: ERROR_TEXT }}>{t.opNoSuppliers}</span>}
                      </td>
                      <td style={{ padding: '0.45rem 0.6rem', whiteSpace: 'nowrap' }}>
                        <button
                          onClick={() => handleDeleteOutsourcedProcess(op.id)}
                          style={{
                            ...BTN_DESTRUCTIVE_OUTLINE,
                            ...FONT_SMALL,
                            borderRadius: 5, padding: '0.2rem 0.4rem',
                            cursor: 'pointer',
                          }}
                        >{t.deleteBtn}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {opFormOpen && (
            <div style={{
              background: BG_TABLE, border: `1px solid ${STATUS_NEUTRAL.bg}`,
              borderRadius: 8, padding: '1rem', marginTop: (data.outsourcedProcesses || []).length > 0 ? '0.5rem' : 0,
            }}>
              <p style={{
                ...FONT_BODY_STRONG,
                color: TEXT_HEADING, margin: '0 0 0.75rem',
              }}>{t.opFormTitleNew}</p>
              <div style={{ marginBottom: '0.6rem' }}>
                <label style={labelSt}>{t.fOpProcess} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                <input
                  type="text" value={opForm.process}
                  onChange={e => setOpForm(f => ({ ...f, process: e.target.value }))}
                  style={inputSt(opFormErr && !opForm.process.trim())}
                />
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={labelSt}>{t.fOpSuppliers} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                <TagEditor
                  tags={opForm.linkedSupplierIds}
                  onChange={val => setOpForm(f => ({ ...f, linkedSupplierIds: val }))}
                  placeholder={t.opSupplierIdPlaceholder}
                />
                {suppliers.length > 0 && (
                  <p style={{ ...helperSt, marginTop: '0.5rem', marginBottom: 0 }}>
                    {t.opSupplierHint}{' '}
                    {suppliers.map(s => s.ref || s.id).filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
              {opFormErr && (
                <p style={{ ...FONT_LABEL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>
                  {t.opValidation}
                </p>
              )}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleAddOutsourcedProcess}
                  style={{
                    ...BTN_PRIMARY,
                    ...FONT_BUTTON,
                    border: 'none',
                    borderRadius: 6, padding: '0.38rem 1rem', cursor: 'pointer',
                  }}
                >{t.opFormSave}</button>
                <button
                  onClick={() => { setOpFormOpen(false); setOpFormErr(false); }}
                  style={{
                    ...BTN_SECONDARY,
                    ...FONT_LABEL,
                    borderRadius: 6,
                    padding: '0.38rem 0.85rem', cursor: 'pointer',
                  }}
                >{t.opFormCancel}</button>
              </div>
            </div>
          )}
        </div>

        {/* Section 8 — Change history */}
        <div style={cardSt}>
          <SectionTitle>
            {t.sChangeHistory}
            <InfoBadge text={t.ttChangeHistory} />
          </SectionTitle>

          {data.changeHistory && data.changeHistory.length > 0 && (
            <div style={{ overflowX: 'auto', marginBottom: '1rem', borderRadius: 6, border: `1px solid ${BORDER_DEFAULT}` }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: SURFACE_WHITE }}>
                <thead>
                  <tr>
                    <th style={thSt}>{t.chColVersion} <span style={{ color: ERROR_TEXT }}>{t.required}</span></th>
                    <th style={thSt}>{t.chColDate} <span style={{ color: ERROR_TEXT }}>{t.required}</span></th>
                    <th style={thSt}>{t.chColAuthor} <span style={{ color: ERROR_TEXT }}>{t.required}</span></th>
                    <th style={{ ...thSt, width: '100%' }}>{t.chColSummary}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.changeHistory.map((entry, idx) => (
                    <tr key={idx}>
                      <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>{entry.version}</td>
                      <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>{entry.date}</td>
                      <td style={tdSt}>{resolvePerson(entry.author, people)}</td>
                      <td style={tdSt}>{entry.summary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add change entry */}
          <div style={{ display: 'grid', gridTemplateColumns: '6rem 8rem 1fr 2fr auto', gap: '0.5rem', alignItems: 'end' }}>
            <div>
              <label style={labelSt}>{t.chColVersion} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
              <input value={chVersion} onChange={e => setChVersion(e.target.value)} placeholder="1.1" style={chInputSt} />
            </div>
            <div>
              <label style={labelSt}>{t.chColDate} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
              <input type="date" value={chDate} onChange={e => setChDate(e.target.value)} style={chInputSt} />
            </div>
            <div>
              <label style={labelSt}>{t.chColAuthor} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
              <PeopleSelector orgId={org.id} lang={lang} value={chAuthor} onChange={val => setChAuthor(val)} disabled={isSaving} />
            </div>
            <div>
              <label style={labelSt}>{t.chColSummary}</label>
              <input value={chSummary} onChange={e => setChSummary(e.target.value)} style={chInputSt} />
            </div>
            <button
              type="button"
              onClick={handleAddChangeEntry}
              disabled={!chVersion.trim() || !chDate || !chAuthor}
              style={{
                ...BTN_PRIMARY,
                background: (!chVersion.trim() || !chDate || !chAuthor) ? TEXT_DISABLED : BTN_PRIMARY.background,
                border: 'none',
                borderRadius: 6, padding: '0.35rem 0.75rem',
                cursor: (!chVersion.trim() || !chDate || !chAuthor) ? 'default' : 'pointer',
                ...FONT_BUTTON,
                whiteSpace: 'nowrap',
              }}
            >
              {t.chAddEntry}
            </button>
          </div>
        </div>

        {/* Section 9 — Version and approval */}
        <div style={cardSt}>
          <SectionTitle>
            {t.sApproval}
            <InfoBadge text={t.ttApproval} />
          </SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelSt}>{t.fVersion}</label>
              <div
                style={{
                  ...inputSt(false),
                  background: BG_SUBTLE,
                  color: TEXT_MUTED,
                  cursor: 'default',
                  userSelect: 'text',
                }}
              >
                {data.version || '—'}
              </div>
            </div>
            <div>
              <label style={labelSt}>{t.fApprovalDate}</label>
              <input
                type="date" value={data.approvalDate || ''}
                onChange={e => set('approvalDate', e.target.value || null)}
                style={inputSt(false)}
              />
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelSt}>{t.fApprovedBy}</label>
            <PeopleSelector orgId={org.id} lang={lang} value={Array.isArray(data.approvedBy) ? data.approvedBy : []} onChange={val => set('approvedBy', val)} multiple />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelSt}>{t.fApprovalEvidenceId}</label>
            <EvidenceSelector
              orgId={org.id}
              lang={lang}
              value={data.approvalEvidenceId}
              onChange={(v) => set('approvalEvidenceId', v)}
            />
          </div>
          <div>
            <label style={labelSt}>{t.fNextReview}</label>
            <input
              type="date" value={data.nextReviewDate || ''}
              onChange={e => set('nextReviewDate', e.target.value)}
              style={{ ...inputSt(isOverdue(data.nextReviewDate)), maxWidth: 220 }}
            />
          </div>
        </div>

      </div>
      </fieldset>
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || 'Confirm'}
        cancelLabel={t.excFormCancel || 'Cancel'}
        secondaryAction={confirmState?.secondaryAction}
      />
      <Divider />

      {/* Sticky action bar */}
      <div style={{
        position: 'sticky', bottom: 0,
        marginLeft: '-1.5rem', marginRight: '-1.5rem', marginBottom: '-2.5rem',
        marginTop: '1.5rem',
        padding: '0.85rem 1.5rem', background: BG_SURFACE,
        borderTop: `1px solid ${BORDER_DEFAULT}`, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          {dirty && (
            <span style={{
              ...FONT_LABEL,
              color: UNSAVED_AMBER, letterSpacing: '0.03em',
            }}>{t.unsavedLabel}</span>
          )}
          <button
            onClick={handleSave}
            disabled={!dirty || saveState === 'saving'}
            style={{
              ...BTN_PRIMARY,
              ...FONT_BUTTON,
              border: 'none',
              borderRadius: 8, padding: '0.45rem 1.1rem',
              cursor: (!dirty || saveState === 'saving') ? 'not-allowed' : 'pointer',
              opacity: !dirty ? 0.45 : 1,
            }}
          >{saveLabel}</button>
        </div>
      </div>
    </div>
  );
}
